import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/** The Hive mark — a simple amber hex, self-contained (no external asset). */
export function BrandMark({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <path
        d="M24 3 42 13.5v21L24 45 6 34.5v-21L24 3Z"
        fill="hsl(var(--accent) / 0.16)"
        stroke="hsl(var(--accent))"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M24 15 33 20v10l-9 5-9-5V20l9-5Z" fill="hsl(var(--accent))" opacity="0.9" />
    </svg>
  );
}

/** Full-screen centered auth layout with the product's ambient gradient + glass. */
export function AuthScreen({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="relative flex min-h-screen w-screen items-center justify-center overflow-hidden bg-[hsl(var(--bg-0))] px-5 py-10 text-app">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[hsl(var(--bg-1))] to-[hsl(var(--bg-0))]" />
      <div
        className="pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(circle, hsl(var(--accent) / 0.35), transparent 70%)" }}
      />
      <main className="glass-strong relative w-full max-w-[400px] rounded-3xl border border-[hsl(var(--border))] p-8 shadow-2xl">
        <div className="mb-6 flex flex-col items-center text-center">
          <BrandMark />
          <h1 className="mt-4 text-xl font-semibold tracking-tight text-app">{title}</h1>
          {subtitle && <p className="mt-1.5 text-sm text-app-1">{subtitle}</p>}
        </div>
        {children}
        {footer && <div className="mt-6 text-center text-sm text-app-2">{footer}</div>}
      </main>
    </div>
  );
}

export const Field = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { label: string }>(
  function Field({ label, id, className, ...props }, ref) {
    const inputId = id ?? props.name;
    return (
      <label htmlFor={inputId} className="mb-3 block text-left">
        <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-app-2">{label}</span>
        <input
          ref={ref}
          id={inputId}
          className={cn(
            "w-full select-text rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] px-3.5 py-2.5 text-sm text-app outline-none transition",
            "placeholder:text-app-2 focus:border-[hsl(var(--accent))] focus:ring-2 focus:ring-[hsl(var(--accent)/0.3)]",
            className,
          )}
          {...props}
        />
      </label>
    );
  },
);

export function PrimaryButton({
  children,
  loading,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }) {
  return (
    <button
      className={cn(
        "flex w-full items-center justify-center gap-2 rounded-xl bg-[hsl(var(--accent))] px-4 py-2.5 text-sm font-semibold text-[hsl(var(--bg-0))] transition",
        "hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent)/0.5)] disabled:opacity-60",
        className,
      )}
      disabled={loading || props.disabled}
      {...props}
    >
      {loading && <Spinner size={15} />}
      {children}
    </button>
  );
}

export function GhostButton({ children, className, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={cn(
        "rounded-xl border border-[hsl(var(--border))] px-4 py-2.5 text-sm font-medium text-app transition hover:bg-[hsl(var(--surface-2))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent)/0.3)]",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function Spinner({ size = 18 }: { size?: number }) {
  return (
    <span
      className="inline-block animate-spin rounded-full border-2 border-current border-t-transparent"
      style={{ width: size, height: size }}
      role="status"
      aria-label="Loading"
    />
  );
}

export function FullScreenLoader({ label = "Loading Hive…" }: { label?: string }) {
  return (
    <div className="flex min-h-screen w-screen items-center justify-center bg-[hsl(var(--bg-0))] text-app-1">
      <div className="flex flex-col items-center gap-3">
        <Spinner size={26} />
        <span className="text-sm">{label}</span>
      </div>
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      className="mb-3 rounded-xl border border-red-500/30 bg-red-500/10 px-3.5 py-2.5 text-left text-sm text-red-300"
    >
      {children}
    </div>
  );
}
